import { doc, setDoc, serverTimestamp } from "firebase/firestore";

/**
 * Cache Manager Utility
 * Helps store and retrieve Firestore Master Data in LocalStorage
 * effectively reducing repeat reads on page refreshes.
 */

const STORAGE_KEYS = {
  SKU_MASTER: "quickbill_cache_sku_master",
  PARTY_MASTER: "quickbill_cache_party_master"
};

/**
 * Returns the cached data if it exists and matches the version.
 * @param {string} key - The cache key (e.g., 'SKU_MASTER')
 * @returns {Array|null} - The cached data or null if invalid/stale
 */
export const getCachedMasterData = (key) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[key]);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error(`Cache Read Error [${key}]:`, err);
    return null;
  }
};

/**
 * Saves master data to cache with a version timestamp.
 * @param {string} key - The cache key
 * @param {Array} data - The array of objects to cache
 * @param {number|string} version - The version identifier (usually Firestore updatedAt timestamp)
 */
export const setCachedMasterData = (key, data, version) => {
  try {
    const cacheObj = {
      data,
      version,
      cachedAt: Date.now()
    };
    localStorage.setItem(STORAGE_KEYS[key], JSON.stringify(cacheObj));
  } catch (err) {
    console.error(`Cache Write Error [${key}]:`, err);
    // LocalStorage might be full
  }
};

/**
 * Helper to update the master metadata timestamp in Firestore.
 * Call this after any write to SKU or Party collections.
 * @param {object} db - Firestore instance
 * @param {string} type - 'sku' or 'party'
 */
export const updateMasterMetadata = async (db, type) => {
  try {
    const field = type === 'sku' ? 'skuLastUpdated' : 'partyLastUpdated';
    await setDoc(doc(db, "metadata", "masterData"), {
      [field]: serverTimestamp()
    }, { merge: true });
  } catch (err) {
    console.warn("Could not update master metadata (ignore if unauthorized):", err);
  }
};
